import React from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useGoogleDriveSourceSettingsText,
  useGoogleDriveSourceSettingsViewModel,
} from "@onetasklist/shared/sources/googledrive";
import { useTranslation } from "react-i18next";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface GoogleDriveSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const GoogleDriveSourceSettings: React.FC<GoogleDriveSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const text = useGoogleDriveSourceSettingsText(t);
  const vm = useGoogleDriveSourceSettingsViewModel({
    editingConnection,
    onUpdate,
    sourceSettingsApi: undefined,
    t,
  });

  if (!vm.isVisible) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t("board.connections.catalogue.sourceSettings.googledrive.sectionTasks")}
    >
      <Field
        orientation="horizontal"
        label={text.starredFilesLabelText}
        hint={text.starredFilesHintText}
      >
        <Checkbox
          checked={vm.isStarredFilesOn}
          onChange={(_, data) => vm.onToggleStarredFiles(Boolean(data.checked))}
        />
      </Field>
      <Field
        orientation="horizontal"
        label={text.assignedCommentsLabelText}
        hint={text.assignedCommentsHintText}
      >
        <Checkbox
          checked={vm.isAssignedCommentsOn}
          onChange={(_, data) => vm.onToggleAssignedComments(Boolean(data.checked))}
        />
      </Field>
      <Field
        orientation="horizontal"
        label={text.sharedWithMeLabelText}
        hint={text.sharedWithMeHintText}
      >
        <Checkbox
          checked={vm.isSharedWithMeOn}
          disabled={!vm.isCoverageEditable}
          onChange={(_, data) => vm.onToggleSharedWithMe(Boolean(data.checked))}
        />
      </Field>
      <Field
        orientation="horizontal"
        label={text.sharedDrivesLabelText}
        hint={text.sharedDrivesHintText}
      >
        <Checkbox
          checked={vm.isSharedDrivesOn}
          disabled={!vm.isCoverageEditable}
          onChange={(_, data) => vm.onToggleSharedDrives(Boolean(data.checked))}
        />
      </Field>
      {vm.hasNothingSelected && <Text>{text.nothingSelectedText}</Text>}
    </SettingsSectionCard>
  );
};
